import { useModal } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useAccounts } from '@modules-deprecated/app/accounts'
import { Account } from '@modules-deprecated/app/accounts/types'

import { ModalId } from '../../../../../../../../../enums/modalId'
import { useForm } from '../../../../../../../../../hooks'
import { useUpdateAccountFromTemplate } from '../../../../../hooks/useUpdateAccountFromTemplate'
import { UpdateAccountFromTemplateFormSchema } from '../../../types'
import {
  getAccountNumberFromAccount,
  getExcludedAccountIds,
  getSelectedBaseAccountId,
} from '../../../utils/accountFromTemplateAccounts'
import { getUpdateAccountFromTemplateDefaultValues } from '../../../utils/getUpdateAccountFromTemplateDefaultValues'
import { getUpdateAccountFromTemplateFormSchema } from '../../../utils/getUpdateAccountFromTemplateFormSchema'

interface UseUpdateAccountFromTemplateModalOptions {
  account?: Account
  onClose?: () => void
  onSave?: () => void
}

export const useUpdateAccountFromTemplateModal = ({
  account,
  onClose,
  onSave,
}: UseUpdateAccountFromTemplateModalOptions) => {
  const { t } = useTranslation()
  const { close: closeUpdateModal } = useModal(ModalId.AccountFromTemplateModalEdit)
  const { accounts } = useAccounts()
  const updateAccountFromTemplateForm = useForm<UpdateAccountFromTemplateFormSchema>({
    defaultValues: getUpdateAccountFromTemplateDefaultValues(account),
    mode: 'onChange',
    validationSchema: getUpdateAccountFromTemplateFormSchema(t),
  })
  const { Form, formState, handleSubmit, reset, watch } = updateAccountFromTemplateForm

  const name = watch('name')
  const excludedAccountIds = useMemo(() => getExcludedAccountIds(accounts ?? []), [accounts])
  const selectedBaseAccountId = useMemo(() => getSelectedBaseAccountId(accounts ?? [], account), [account, accounts])

  const resetForm = useCallback(() => {
    reset(getUpdateAccountFromTemplateDefaultValues(account))
  }, [account, reset])

  const closeModal = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      closeUpdateModal()
    }

    resetForm()
  }, [closeUpdateModal, onClose, resetForm])

  const { isProcessing: isUpdating, updateAccount } = useUpdateAccountFromTemplate({
    onSuccess: () => {
      onSave?.()
      closeModal()
    },
  })

  const submitForm = useCallback(() => {
    if (!account) {
      return
    }

    handleSubmit(({ description, name: submittedName }) => {
      updateAccount({ accountId: account.id, description, name: submittedName })
    })()
  }, [account, handleSubmit, updateAccount])

  return {
    accountNumber: getAccountNumberFromAccount(account),
    closeModal,
    excludedAccountIds,
    Form,
    isResetDisabled: !formState.isDirty,
    isSaveDisabled: !account || !formState.isDirty || !name.trim() || isUpdating,
    isUpdating,
    resetForm,
    selectedBaseAccountId,
    submitForm,
    updateAccountFromTemplateForm,
  }
}
